import { red } from '@mui/material/colors';
import TextField from '@mui/material/TextField';
import { format } from '@react-input/mask';
import { phoneNumberValidator } from '@thesavingsgroup/constants/regex';
import toString from 'lodash/toString';
import trim from 'lodash/trim';
import { ChangeEvent, Fragment, ReactNode } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { inputEventHandler } from '../utils/inputEventHandler';
import { PhoneNumberInputProps } from './types/PhoneNumberInputProps';

export const PhoneNumberInput = <
  TFieldValues extends FieldValues = FieldValues,
>({
  control,
  name,
  inputLabelKey,
  slotProps,
  validate,
  errorTestId,
}: PhoneNumberInputProps<TFieldValues>): ReactNode => {
  const { t } = useTranslation();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
          // On some browsers (i.e. iOS Chrome) the autofill feature triggers the onChange event
          // at an unexpected time, causing the value to be set to the unformatted value. By using
          // setTimeout, we can ensure that the value is set to the formatted value on the next tick
          // (after the browser has finished autofilling the input). For this reason we rely on the
          // format utility function, rather than using the useMask hook.

          const formattedValue = format(event.target.value, {
            mask: '(___) ___-____',
            replacement: { _: /\d/ },
          });

          setTimeout((): void => {
            const parsedValue = trim(
              inputEventHandler.phoneNumber.parser(formattedValue),
            );

            field.onChange(parsedValue);
            event.target.value = formattedValue;
          }, 0);
        };

        return (
          <Fragment>
            <TextField
              {...field}
              autoComplete="tel-national"
              color="primary"
              error={Boolean(error)}
              label={t(inputLabelKey)}
              slotProps={{
                ...slotProps,
                htmlInput: {
                  ...slotProps?.htmlInput,
                  inputMode: 'numeric',
                  maxLength: 14,
                },
              }}
              value={inputEventHandler.phoneNumber.formatter(field.value)}
              variant="outlined"
              fullWidth
              onChange={onChange}
            />

            {error && (
              <span
                data-testid={errorTestId}
                style={{ fontSize: '12px', color: red[700] }}
              >
                {toString(error.message)}
              </span>
            )}
          </Fragment>
        );
      }}
      rules={{
        required: t('validation.phone.required'),
        validate: (value: unknown): string | boolean => {
          const stringValue = String(value ?? '');

          if (!phoneNumberValidator.test(stringValue)) {
            return t('validation.phone.invalid');
          }

          if (validate) {
            return validate(stringValue);
          }

          return true;
        },
      }}
    />
  );
};
